import { FC, useMemo } from 'react';
import Card from '@/components/card';
import Chip from '@/components/chip';
import PublicServiceButton from '@/components/public-service-button';
import Text from '@/components/text';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { Box, Stack } from '@mui/material';
import { GroupMeta } from '@/lib/models/group.model';
import { ContextProviderMeta } from '@/lib/models/context-provider';
import useAppData from '@/hooks/use-app-data.hook';
import CardHeader from '../card/card-header';
import { Link } from '@tanstack/react-router';
import { AppDataQueryKey } from '@/lib/query-client';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import ScrollBox from '../scroll-box';
import { AgentMeta } from '@/lib/models/agent.model';
import { getPolicyLink } from '../policy-link/helpers';
import useFeature from '@/hooks/use-feature.hook';
import { PolicyMeta } from '@/lib/models/policy-model';
import { PolicyMeta as PolicyMetaV1 } from '@/lib/models/policy-model-v1';
import { PublicServiceConfig } from '@/lib/models/policy-model/public-services';
import { PublicServiceConfig as PublicServiceConfigV1 } from '@/lib/models/policy-model-v1/public-services';

interface Props {
  policy: PolicyMeta | PolicyMetaV1;
  groups?: GroupMeta[];
  contextProviders?: ContextProviderMeta[];
  privateModels?: PrivateModelModel[];
  agents?: AgentMeta[];
}

interface CardSectionProps {
  title: string;
  items: { id: string | null; name: string }[];
}

const CardSection: FC<CardSectionProps> = ({ title, items }) => {
  return (
    <Box mt={2.5}>
      <Box mb={1}>
        <Text size="small">{title}</Text>
      </Box>

      <Stack direction="row" gap={1} flexWrap="wrap">
        {items.map(({ id, name }) => {
          return <Chip key={id} label={name} />;
        })}
      </Stack>
    </Box>
  );
};

const PolicyCard: FC<Props> = ({
  policy,
  groups = [],
  contextProviders = [],
  privateModels = [],
  agents = [],
}) => {
  const { hasPolicyV2 } = useFeature();
  const { name, scope, isDefault } = policy;
  const isOrg = scope === 'org';
  const hasGroups = !!groups.length;
  const hasContext = !!contextProviders.length;
  const hasModels = !!privateModels.length;
  const hasAgents = !!agents.length;
  const link = getPolicyLink(policy, hasPolicyV2);

  const { data: allPublicServices = [] } = useAppData<PublicServiceMeta[]>(
    AppDataQueryKey.PublicServices
  );

  const validPublicServices = useMemo(() => {
    const { publicServices } = policy;

    if (hasPolicyV2) {
      const { serviceAccess = [] } = policy.publicServices as unknown as PublicServiceConfig;

      const allowedServices = serviceAccess
        .filter(({ action }) => action !== 'blocked')
        .map(({ serviceId }) => serviceId);

      return allPublicServices.filter(({ id }) => allowedServices.includes(id));
    }

    const { enabled } = publicServices as PublicServiceConfigV1;

    return allPublicServices.filter(({ id }) => enabled?.includes(id));
  }, [allPublicServices, hasPolicyV2, policy]);

  return (
    <Card>
      <CardHeader title={isOrg ? 'Default' : name} icon={isOrg ? 'policy' : 'groups'}>
        <Link to={link}>
          <Text size="small">View Policy</Text>
        </Link>
      </CardHeader>

      <Box>
        <Box mb={1}>
          <Text size="small">Allowed Public Services</Text>
        </Box>
        <ScrollBox max={4} emptyMessage="This policy does not grant access to any public services.">
          {validPublicServices.map((publicService) => {
            return <PublicServiceButton publicService={publicService} key={publicService.id} />;
          })}
        </ScrollBox>

        {hasContext && <CardSection title="Assigned Data Sources" items={contextProviders} />}

        {hasModels && <CardSection title="Enabled Portal Private Models" items={privateModels} />}

        {hasAgents && <CardSection title="Assigned Assistants" items={agents} />}

        {!isDefault && hasGroups && <CardSection title="Assigned Groups" items={groups} />}
      </Box>
    </Card>
  );
};

export default PolicyCard;
